// @ts-nocheck
/*
 * Port of views/dashboard/licence_history/list.js (origin/main-dev, 707 lines).
 * BYTE-IDENTICAL LOGIC â€” state hooks, useEffects (with companyId/
 * callcenterId deps), fetch flow with query-string builder, licence-diff
 * card rendering, action-badge colors, date range default (first-of-
 * month â†’ today, 6-months-ago min), and dual render mode preserved.
 *
 * Dual render mode preserved: when invoked with `companyId` or
 * `callcenterId` props (embedded inside a modal) it renders a compact
 * table WITHOUT search/date/export toolbar; otherwise renders as the
 * standalone Licence History page.
 *
 * Chrome swaps:
 *   - PrimeReact <DataTable> + <Column> â†’ obd3-gui DataTable + ColumnDef
 *   - PrimeReact <Button> â†’ shadcn <Button>
 *   - v1 <Card> â†’ shadcn <Card>
 */
import React, { useState, useEffect, useMemo } from 'react';
const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall } from '@/service/httpCall';
import { LICENCE_HISTORY_LIST } from '@/service/url';
import Loader from '@/components/partials/components/Loader';
import { errorCheck } from '@/views/dashboard/auth/auth-utils';
import { useNavigate } from 'react-router-dom';
import { LICENCE_LABEL } from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { Download, ArrowUpCircle, FileText, User, Building2, UserCheck, UserX } from 'lucide-react';
import {
  FaBroadcastTower,
  FaBuilding,
  FaUsers,
  FaUserCheck,
  FaPhone,
  FaArrowRight,
  FaTachometerAlt,
  FaMobileAlt,
} from 'react-icons/fa';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { createPortal } from 'react-dom';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import { usePersistedState } from '@/lib/filters/persisted-state';

const getTodayStr = () => new Date().toLocaleDateString('en-CA');
const getFirstOfMonthStr = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString('en-CA');
};
const getSixMonthsAgoStr = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return d.toLocaleDateString('en-CA');
};

const safeDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const ICON_MAP = {
  channel: <FaBroadcastTower />,
  company: <FaBuilding />,
  live_agent: <FaUserCheck />,
  role_ccagent: <FaUsers />,
  role_teamlead: <FaUserCheck />,
  role_ccadmin: <FaPhone />,
  cps: <FaTachometerAlt />,
  mobile: <FaMobileAlt />,
};

/**
 * Custom hover popover for the "+N more" pill on Licence History rows.
 *
 * Radix Tooltip kept mis-firing inside the table's `overflow-x: auto`
 * wrapper (portal missing in the local shadcn TooltipContent, and the
 * `asChild` span was intermittently dropping pointerenter). This is a
 * dead-simple replacement:
 *   - React state for open/close driven by pointerenter/pointerleave
 *   - createPortal to <body> so nothing about the DataTable wrapper can
 *     clip us
 *   - Absolute-positioned panel anchored to the trigger via
 *     getBoundingClientRect + scroll offsets, re-measured on scroll and
 *     window resize while open so it stays glued to the pill.
 *   - Small enter/leave delay + shared timer between trigger and panel
 *     so the pointer can travel from the pill to the panel without
 *     the panel snapping shut mid-hop.
 */
function MoreChangesHover({
  trigger,
  children,
}: {
  trigger: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; height: number } | null>(null);
  const triggerRef = React.useRef<HTMLSpanElement | null>(null);
  const closeTimer = React.useRef<number | null>(null);

  const measure = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({ top: rect.top, left: rect.left, height: rect.height });
  }, []);

  const cancelClose = () => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const handleOpen = () => {
    cancelClose();
    measure();
    setOpen(true);
  };

  const handleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => measure();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, measure]);

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        onFocus={handleOpen}
        onBlur={handleClose}
        tabIndex={0}
        className="inline-block cursor-help"
      >
        {trigger}
      </span>
      {open && pos &&
        createPortal(
          <div
            onMouseEnter={cancelClose}
            onMouseLeave={handleClose}
            style={{
              // FIXED, not absolute: positioned relative to the viewport
              // so no ancestor overflow / transform / stacking context
              // can affect us. Coordinates come straight from
              // getBoundingClientRect (already viewport-relative), so no
              // scroll math is involved either.
              position: 'fixed',
              // 8-px gap above the trigger. If the trigger sits near the
              // top of the viewport we fall back to below the trigger.
              top:
                pos.top > 260
                  ? undefined
                  : pos.top + (pos.height || 0) + 8,
              bottom:
                pos.top > 260
                  ? window.innerHeight - pos.top + 8
                  : undefined,
              left: Math.max(8, Math.min(pos.left, window.innerWidth - 540)),
              zIndex: 9999,
              backgroundColor: '#ffffff',
              boxShadow:
                '0 20px 40px -12px rgba(15, 23, 42, 0.35), 0 4px 12px rgba(15, 23, 42, 0.15)',
            }}
            className="w-[min(420px,calc(100vw-1rem))] max-w-[520px] rounded-lg border border-primary/25 p-3 text-foreground dark:!bg-slate-900 dark:!text-slate-100"
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * Full licence-field breakdown used inside the hover popover.
 * Layout matches the reference screenshot: every field on its own row,
 * label on the left, old → new on the right; changed rows carry a
 * green (up) or rose (down) background so they stand out from the
 * quiet, unchanged rows.
 */
function LicenceFullBreakdown({ rows }: { rows: any[] }) {
  return (
    <div className="flex flex-col divide-y divide-slate-200 overflow-hidden rounded-md border border-slate-200 text-xs dark:divide-slate-700 dark:border-slate-700">
      {rows.map(({ key, oldVal, newVal, delta }) => {
        const up = delta > 0;
        const down = delta < 0;
        const changed = delta !== 0;
        return (
          <div
            key={key}
            className={
              'flex items-center justify-between gap-3 px-3 py-1.5 ' +
              (up
                ? 'bg-emerald-50 dark:bg-emerald-950/30'
                : down
                ? 'bg-rose-50 dark:bg-rose-950/30'
                : 'bg-white dark:bg-slate-900')
            }
          >
            <span className={'flex items-center gap-2 ' + (changed ? 'font-semibold text-foreground' : 'text-muted-foreground')}>
              <span className={changed ? (up ? 'text-emerald-600' : 'text-rose-600') : 'text-muted-foreground/70'} aria-hidden="true">
                {ICON_MAP[key]}
              </span>
              {LICENCE_LABEL[key]}
            </span>
            <span className="flex items-center gap-1.5 tabular-nums">
              <span
                className={
                  'rounded px-1.5 py-0.5 text-[11px] ' +
                  (changed
                    ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/60 dark:text-rose-200'
                    : 'text-muted-foreground')
                }
              >
                {oldVal}
              </span>
              <FaArrowRight
                size={9}
                color="currentColor"
                className={changed ? (up ? 'text-emerald-600' : 'text-rose-600') : 'text-muted-foreground/70'}
              />
              <span
                className={
                  'rounded px-1.5 py-0.5 text-[11px] ' +
                  (changed
                    ? 'bg-emerald-100 font-semibold text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-200'
                    : 'text-muted-foreground')
                }
              >
                {newVal}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function LicenceHistoryList({ companyId, callcenterId, displayName }: { companyId?: any; callcenterId?: any; displayName?: any } = {}) {
  const navigate = useNavigate();
  const [operationList, setOperationList] = useState([]);
  const [originalDataList, setOriginalDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  // Only the standalone page (licence-history.page) has filter inputs; the embedded
  // modal view (licence-history.embedded) always opens on the default range, unsaved.
  const embedded = Boolean(companyId || callcenterId);
  const filterKey = (field) => (embedded ? null : `licence-history/LicenceHistory.page:${field}`);
  const [searchValue, setSearchValue] = usePersistedState(filterKey('searchValue'), '');

  const [fromDateValue, setFromDateValue] = usePersistedState(filterKey('fromDate'), getFirstOfMonthStr());
  const [toDateValue, setToDateValue] = usePersistedState(filterKey('toDate'), getTodayStr());
  const [appliedFilters, setAppliedFilters] = usePersistedState(filterKey('appliedFilters'), {
    from: getFirstOfMonthStr(),
    to: getTodayStr(),
  });

  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      const changeKeys = Array.from(
        operationList.reduce((keys: Set<string>, row: any) => {
          Object.keys(row.old_licence || {}).forEach((k) => keys.add(k));
          Object.keys(row.updated_licence || {}).forEach((k) => keys.add(k));
          return keys;
        }, new Set<string>()),
      );

      const changeColumns = changeKeys.map((key) => ({
        header: LICENCE_LABEL[key] || key,
        accessor: (row: any) => {
          const oldLic = row.old_licence || {};
          const newLic = row.updated_licence || {};
          if (!(key in oldLic) && !(key in newLic)) return '-';
          return `${oldLic[key] ?? 0} → ${newLic[key] ?? 0}`;
        },
      }));

      exportToCSV({
        fileName: 'licence-history',
        data: operationList,
        columns: [
          { header: '#', accessor: 'key' },
          { header: 'Action', accessor: (row) => row.action?.toUpperCase() || '-' },
          { header: 'Username', accessor: (row) => row.user?.username || '-' },
          { header: 'Company', accessor: (row) => row.company?.name || '-' },
          { header: 'Call Center', accessor: (row) => row.callcenter?.name || '-' },
          {
            header: 'Changes',
            accessor: (row) => {
              const oldLic = row.old_licence || {};
              const newLic = row.updated_licence || {};
              return Object.keys(newLic)
                .map((key) => `${key}: ${oldLic[key] ?? 0} -> ${newLic[key] ?? 0}`)
                .join(' | ');
            },
          },
          { header: 'Date & Time', accessor: 'created_at_formatted' },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const fetchLicenceHistory = async (
    start = fromDateValue,
    end = toDateValue,
    cId = companyId,
    ccId = callcenterId,
  ) => {
    setLoading(true);
    const queryParams = new URLSearchParams();
    if (start) queryParams.append('start_date', start);
    if (end) queryParams.append('end_date', end);
    if (cId) queryParams.append('company_id', cId);
    if (ccId) queryParams.append('callcenter_id', ccId);
    const finalUrl = `${LICENCE_HISTORY_LIST}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    try {
      const resp = await getApiCall(finalUrl);
      const data = Array.isArray(resp) ? resp : resp?.data || [];
      onOperationListSuccess(data);
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = () => {
    fetchLicenceHistory(fromDateValue, toDateValue);
    setAppliedFilters({ from: fromDateValue, to: toDateValue });
  };

  useEffect(() => {
    setOperationList([]);
    setOriginalDataList([]);
    // Standalone page: load the restored applied range; embedded: default range.
    const start = embedded ? getFirstOfMonthStr() : appliedFilters.from;
    const end = embedded ? getTodayStr() : appliedFilters.to;
    if (embedded) {
      setFromDateValue(start);
      setToDateValue(end);
      setAppliedFilters({ from: start, to: end });
    }
    fetchLicenceHistory(start, end, companyId, callcenterId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, callcenterId]);

  function onOperationListSuccess(resp) {
    const updatedList = resp.map((val, idx) => ({
      ...val,
      key: idx + 1,
      company: val.company ? val.company : { name: displayName || '-' },
      created_at_formatted: new Date(val.created_at).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      }),
    }));
    setOperationList(updatedList);
    setOriginalDataList(updatedList);
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState({});

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user?.acl_settings) {
        setModuleOperationsForReadPermissionCheck(
          parseModuleOperations(user.acl_settings.module_operations),
        );
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  // Renders the full old → new + signed-delta chip (previous "detailed"
  // layout). Used for the primary change and inside the "+N more" hover
  // popover so both surfaces speak the same visual language.
  const renderDetailedChip = ({ key, oldVal, newVal, delta }) => {
    const up = delta > 0;
    return (
      <span
        key={key}
        className={
          'inline-flex items-center gap-2 whitespace-nowrap rounded-md border px-2.5 py-1 text-xs ' +
          (up
            ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/30'
            : 'border-rose-200 bg-rose-50 dark:border-rose-900/60 dark:bg-rose-950/30')
        }
      >
        <span className="flex items-center gap-1.5 font-medium text-foreground">
          <span className={up ? 'text-emerald-600' : 'text-rose-600'} aria-hidden="true">
            {ICON_MAP[key]}
          </span>
          {LICENCE_LABEL[key]}
        </span>
        <span className="flex items-center gap-1.5 tabular-nums">
          <span className="text-muted-foreground">{oldVal}</span>
          <FaArrowRight
            size={10}
            color="currentColor"
            className={up ? 'text-emerald-600' : 'text-rose-600'}
          />
          <span className={'font-semibold ' + (up ? 'text-emerald-700' : 'text-rose-700')}>
            {newVal}
          </span>
          <span
            className={
              'ml-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold ' +
              (up
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300'
                : 'bg-rose-100 text-rose-700 dark:bg-rose-900/60 dark:text-rose-300')
            }
          >
            {up ? '+' : ''}
            {delta}
          </span>
        </span>
      </span>
    );
  };

  const licenceChangeBody = (rowData) => {
    const oldLic = rowData.old_licence || {};
    const newLic = rowData.updated_licence || {};

    // Two lists off the same source-of-truth:
    //   all      → every licence field for this record (populates hover)
    //   changed  → only fields where the value actually moved
    // Inline cell shows the compact changed chips; hover popover shows
    // the FULL breakdown (changed rows highlighted, unchanged rows
    // muted), matching the "all fields visible" layout expected here.
    const all = Object.keys(LICENCE_LABEL)
      .filter((key) => {
        if (rowData.callcenter && key === 'role_ccadmin') return false;
        if (rowData.callcenter && key === 'company') return false;
        return true;
      })
      .map((key) => {
        const oldVal = Number(oldLic[key] ?? 0);
        const newVal = Number(newLic[key] ?? 0);
        return { key, oldVal, newVal, delta: newVal - oldVal };
      });

    const changed = all.filter((r) => r.delta !== 0);

    if (changed.length === 0) {
      return <span className="text-xs text-muted-foreground">No licence changes</span>;
    }

    // Show ONE detailed chip inline (the first change). If more fields
    // moved, append a small muted `+N` glyph so it's obvious there's
    // more to see. Hovering anywhere on the cell reveals the full
    // breakdown popover (every field, changed rows tinted).
    const [primary, ...rest] = changed;
    return (
      <MoreChangesHover
        trigger={
          <span className="inline-flex items-center gap-1.5">
            {renderDetailedChip(primary)}
            {rest.length > 0 && (
              <span className="text-[11px] font-medium text-muted-foreground">
                +{rest.length}
              </span>
            )}
          </span>
        }
      >
        <LicenceFullBreakdown rows={all} />
      </MoreChangesHover>
    );
  };

  const tableRows = useMemo(() => {
    const list = Array.isArray(originalDataList) ? originalDataList : [];
    return list
      .map((r, idx) => {
        const createdDate = safeDate(r?.created_at);
        const createdTs = createdDate ? createdDate.getTime() : 0;
        return {
          ...r,
          key: r?.key ?? idx + 1,
          _createdTs: createdTs,
          _createdFromDate: createdDate,
        };
      })
      .sort((a, b) => (b._createdTs || 0) - (a._createdTs || 0));
  }, [originalDataList]);

  const filteredRows = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    const start = appliedFilters.from
      ? new Date(`${appliedFilters.from}T00:00:00.000`).getTime()
      : null;
    const end = appliedFilters.to
      ? new Date(`${appliedFilters.to}T23:59:59.999`).getTime()
      : null;

    return tableRows.filter((r) => {
      if (start != null && (r?._createdTs || 0) < start) return false;
      if (end != null && (r?._createdTs || 0) > end) return false;

      if (query) {
        const action = r?.action?.toLowerCase() || '';
        const username = r?.user?.username?.toLowerCase() || '';
        const company = r?.company?.name?.toLowerCase() || '';
        const callcenter = r?.callcenter?.name?.toLowerCase() || '';
        const created = r?.created_at_formatted?.toLowerCase() || '';

        if (
          action.includes(query) ||
          username.includes(query) ||
          company.includes(query) ||
          callcenter.includes(query) ||
          created.includes(query)
        ) {
          return true;
        }

        const oldLic = r.old_licence || {};
        const newLic = r.updated_licence || {};
        for (const key of Object.keys(LICENCE_LABEL)) {
          if (String(oldLic[key] ?? '').toLowerCase().includes(query)) return true;
          if (String(newLic[key] ?? '').toLowerCase().includes(query)) return true;
        }

        return false;
      }
      return true;
    });
  }, [searchValue, tableRows, appliedFilters]);

  // Shared tone-tinted action pill (used by both standalone and embedded).
  // Kept as a single function so any tone/icon change stays in one place.
  const actionBadge = (rowData) => {
    const raw = rowData.action || '';
    const action = raw.toLowerCase();
    const isCreated =
      action === 'created' ||
      action === 'agent_created' ||
      action === 'bulk_agent_created' ||
      action === 'ai_agent_created' ||
      action === 'ai_agent_restored';
    const isDeleted = action === 'agent_deleted' || action === 'ai_agent_deleted';
    const isLiveLogin = action === 'live_agent_login';
    const isLiveLogout = action === 'live_agent_logout';
    const isChannelDeducted = action === 'channel_usage';

    const tone = isCreated
      ? 'bg-sky-100 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300'
      : isDeleted
      ? 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300'
      : isLiveLogin
      ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
      : isLiveLogout
      ? 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300'
      : isChannelDeducted
      ? 'bg-violet-100 text-violet-700 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300'
      : 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300';

    const Icon = isCreated
      ? FileText
      : isDeleted
      ? UserX
      : isLiveLogin
      ? UserCheck
      : isLiveLogout
      ? UserX
      : isChannelDeducted
      ? FaBroadcastTower
      : ArrowUpCircle;

    // "LIVE_AGENT_LOGIN" -> "Live agent login" — quieter to read at a glance.
    const label = raw
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/^./, (c) => c.toUpperCase());

    return (
      <span
        className={
          'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
          tone
        }
      >
        <Icon size={12} />
        {label || '—'}
      </span>
    );
  };

  if (loading) {
    return <Loader />;
  }

  // Compact/embedded view: rendered inside Company/Call Center list modals.
  if (companyId || callcenterId) {
    const embeddedColumns: ColumnDef<any>[] = [
      { accessorKey: 'key', header: '#', enableGlobalFilter: false },
      {
        id: 'action',
        header: 'Action',
        accessorFn: (row) => {
          const raw = String(row.action || '');
          return `${raw} ${raw.replace(/_/g, ' ')}`;
        },
        cell: ({ row }) => actionBadge(row.original),
      },
      {
        id: 'changes',
        header: 'Changes',
        enableSorting: false,
        accessorFn: (row) => {
          const oldLic = row.old_licence || {};
          const newLic = row.updated_licence || {};
          return Object.keys({ ...oldLic, ...newLic })
            .map((k) => LICENCE_LABEL[k] || k)
            .join(' ');
        },
        cell: ({ row }) => licenceChangeBody(row.original),
      },
      {
        id: 'company',
        header: 'Company',
        accessorFn: (row) => row.company?.name || row.callcenter?.name || row.name || '',
        cell: ({ row }) => {
          const rowData = row.original;
          const nm = rowData.company?.name || rowData.callcenter?.name || rowData.name;
          return <span className="text-sm text-foreground">{nm || '—'}</span>;
        },
      },
      {
        accessorKey: 'created_at_formatted',
        header: 'Date & Time',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
            {row.original.created_at_formatted || '—'}
          </span>
        ),
      },
    ];

    return (
      <>
        <div className="table-responsive broad-scrollbar" style={{ overflowX: 'auto' }}>
          <DataTable
            tableId="licence-history.embedded"
            columns={embeddedColumns}
            data={tableRows}
            emptyTitle="No licence history"
            emptyDescription="No licence history found."
            pageSize={10}
            disableExport
          />
        </div>
        <style>{`
          .broad-scrollbar::-webkit-scrollbar { height: 10px; }
          .broad-scrollbar::-webkit-scrollbar-thumb { background-color: #888; border-radius: 10px; }
        `}</style>
      </>
    );
  }

  // Standalone page columns
  const pageColumns: ColumnDef<any>[] = [
    { accessorKey: 'key', header: '#' },
    {
      id: 'action',
      header: 'Action',
      cell: ({ row }) => actionBadge(row.original),
    },
    {
      id: 'changes',
      header: 'Changes',
      enableSorting: false,
      cell: ({ row }) => licenceChangeBody(row.original),
    },
    {
      id: 'username',
      header: 'Username',
      cell: ({ row }) => (
        <span className="text-sm text-foreground">
          {row.original.user?.username || '—'}
        </span>
      ),
    },
    {
      id: 'company',
      header: 'Company',
      cell: ({ row }) => (
        <span className="text-sm text-foreground">
          {row.original.company?.name || '—'}
        </span>
      ),
    },
    {
      id: 'callcenter',
      header: 'Call Center',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.callcenter?.name || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'created_at_formatted',
      header: 'Date & Time',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
          {row.original.created_at_formatted || '—'}
        </span>
      ),
    },
  ];

  const isEmbedded = Boolean(companyId || callcenterId);

  const body = (
    <Card className={isEmbedded ? 'p-4' : 'mt-4 p-4'}>
      <DateRangeFilter
        className="mb-4"
        value={{ from: fromDateValue, to: toDateValue }}
        onChange={({ from, to }) => {
          setFromDateValue(from);
          setToDateValue(to);
        }}
        min={getSixMonthsAgoStr()}
        max={getTodayStr()}
        onApply={handleFilterClick}
        actions={
          <>
          <Button
            size="sm"
            variant="secondary"
            className="h-10"
            onClick={() => {
              const start = getFirstOfMonthStr();
              const end = getTodayStr();
              setSearchValue('');
              setFromDateValue(start);
              setToDateValue(end);
              setAppliedFilters({ from: start, to: end });
              fetchLicenceHistory(start, end);
            }}
          >
            Refresh
          </Button>
          {operationList.length > 0 && (
            <Button
              onClick={handleExport}
              disabled={buttonDisable}
              size="sm"
              variant="outline"
              className="h-10 gap-1.5"
            >
              {buttonDisable ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <Download size={14} />
              )}
              Export
            </Button>
          )}
          </>
        }
      >
        <div className="min-w-[220px] flex-1">
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Search
          </label>
          <Form.Control
            type="text"
            placeholder="Search action, user, company, call center…"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{ height: '40px' }}
          />
        </div>
      </DateRangeFilter>

            <div className="table-responsive broad-scrollbar" style={{ overflowX: 'auto' }}>
              <DataTable
                tableId="licence-history.page"
                columns={pageColumns}
                data={filteredRows}
                emptyTitle="No licence history"
                emptyDescription="No licence history found."
                pageSize={10}
                disableSearch
                disableExport
              />
            </div>
    </Card>
  );

  if (isEmbedded) {
    return (
      <>
        {body}
        <style>{`
          .broad-scrollbar::-webkit-scrollbar { height: 10px; }
          .broad-scrollbar::-webkit-scrollbar-thumb { background-color: #888; border-radius: 10px; }
        `}</style>
      </>
    );
  }

  return (
    <FillPage>
      <PageHeader
        title="Licence History"
        description="History of licence allocations and adjustments for this tenant."
      />
      {body}
      <style>{`
        .broad-scrollbar::-webkit-scrollbar { height: 10px; }
        .broad-scrollbar::-webkit-scrollbar-thumb { background-color: #888; border-radius: 10px; }
      `}</style>
    </FillPage>
  );
}
